import { Injectable } from '@angular/core';
import { OrderApiService } from './order.api.service';
import {BehaviorSubject, Observable} from 'rxjs';
import { map } from 'rxjs/operators';
import { Restaurant, RestaurantCategory, RestaurantProduct } from '../models/restaurant.model';
import { RestaurantService } from './restaurant.service';
import { RestaurantStore } from '../stores/restaurant.store';
import { SERVICE_ITEM_MERCHANTS, SERVICE_ITEM_LOCATIONS } from '../constants';
import { RouteService } from './route.service';
import { CartStore } from '../stores/cart.store';
import {EstimateOrder} from '../models/order.model';
import { dataToEstimateOrders, isModifierSoldOut } from '../dto/restaurant.dto';

@Injectable({
  providedIn: 'root'
})
export class ItemService {
  public itemServiceProduct: RestaurantProduct = new RestaurantProduct();

  constructor(
    private apiService: OrderApiService,
    private restaurantService: RestaurantService,
    private restaurantStore: RestaurantStore,
    private routeService: RouteService,
    private cartStore: CartStore
  ) { }

  public getServiceItems(merchantId: string, locationId: string): Observable<RestaurantProduct> {
    return this.apiService.get(`merchants/${merchantId}/locations/${locationId}/catalog/items/checkout`)
      .pipe(map(response => {
        const product = this.restaurantService.convertDataToRestaurantProduct(
          locationId,
          response,
          this.restaurantStore.restaurantModifiers,
          new RestaurantCategory()
        );
        product.isService = true;
        this.itemServiceProduct = product;
        return product;
      }));
  }
  public checkMerchantId(): boolean {
    return SERVICE_ITEM_MERCHANTS.includes(this.routeService.getMerchantId());
  }
  public checkLocationId(locationId: string): boolean {
    return SERVICE_ITEM_LOCATIONS.includes(locationId);
  }
  public checkServiceItem(): boolean {
    return this.cartStore.cartItems.some(item => item.isService);
  }

  public estimateThrottling(locationId: string, estimateObject: EstimateOrder[]): Observable<EstimateOrder[]> {
    return this.apiService.post(`locations/${locationId}/orders/estimate`, estimateObject).pipe(
      map(response => dataToEstimateOrders(response))
    );
  }

  public getSuggestionItems(locationId: string, items: string[]): Observable<string[]> {
    return this.apiService.post(`locations/${locationId}/addons`, items);
  }

  public isSoldOut(product: RestaurantProduct, restaurant: Restaurant): boolean {
    return (this.isProductSold(product, restaurant) && this.getQuantity(product) <= 0) || this.allSoldOut(product) || isModifierSoldOut(product);
  }

  public isProductSold(product: RestaurantProduct, restaurant: Restaurant): boolean {
    const variationsAvailable = [];
    for (const variation of product.variations) {
      if (variation.trackInventory && (restaurant?.enforceInventory || variation.useInventory)) {
        if (variation.quantityRemaining > 0) {
          variationsAvailable.push(variation);
        }
      } else {
        return false;
      }
    }
    return variationsAvailable.length === 0;
  }

  public getQuantity(product: RestaurantProduct): number {
    let quantity = 0;
    for (const variation of product.variations) {
      if (variation.quantityRemaining === null) {
        return Number.MAX_VALUE;
      } else if (variation.quantityRemaining > 0) {
        quantity += variation.quantityRemaining;
      }
    }
    return quantity;
  }

  public allSoldOut(product: RestaurantProduct): boolean {
    return product.variations.filter(variation => !variation.soldOut).length === 0;
  }
}
